import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import api from '../../../services/api';
import { formatDateTime } from '../../../utils/Utils';
import PersonCell from '../../../components/ui/PersonCell';

// Task 32 — GET v1/performance-tasks (taskType[eq]/status[eq], PaginatedList)
// + POST /{id}/complete (Task 17). itiraz incelemesi (objection_review) ve
// gelişmeli takip (needs_improvement_follow_up) görevleri — Task 13'ün
// otomatik ürettiği kayıtlar.
// B2 partisi (PMB-06) — comp_recommendation_recheck: kabul edilen bir itiraz,
// kesinleşmiş ya da kapanmış turdaki ücret satırının dayanağını değiştirdiğinde
// açılan İK görevi (ObjectionDataAccess.RefreshCompRecommendationsAsync).
const TASK_TYPES = ['objection_review', 'needs_improvement_follow_up', 'comp_recommendation_recheck'];
const TASK_STATUSES = ['open', 'done'];

const StatusBadge = styled.span`
  display: inline-block;
  padding: 4px 10px;
  border-radius: 12px;
  font-size: ${({ theme }) => theme.metrics.font.size.xs};
  font-weight: ${({ theme }) => theme.metrics.font.weight.semiBold};
  background: ${({ $done, theme }) => `${$done ? theme.colors.auxiliar.success : theme.colors.auxiliar.warning}22`};
  color: ${({ $done, theme }) => ($done ? theme.colors.auxiliar.success : theme.colors.auxiliar.warning)};
`;

const usePerformanceTasksApi = (queryParams) => {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;

  const [fetching, setFetching] = useState(true);
  const [data, setData] = useState([]);
  const [pagination, setPagination] = useState({ totalRowCount: 0, pageSize: 20, pageIndex: 0 });
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  const refresh = useCallback(() => setRefreshTrigger((p) => p + 1), []);

  useEffect(() => {
    const params = queryParams && Object.keys(queryParams).length > 0
      ? queryParams
      : { pageNumber: 1, pageSize: 20 };
    setFetching(true);
    api.get(`/v1/performance-tasks?${new URLSearchParams(params)}`)
      .then((res) => {
        const result = res.data?.data;
        const items = Array.isArray(result) ? result : (result?.data || []);
        setData(items);
        setPagination({
          totalRowCount: result?.totalRowCount || items.length,
          pageIndex: result?.pageIndex ? result.pageIndex - 1 : 0,
          pageSize: result?.pageSize || items.length || 20,
        });
      })
      .finally(() => setFetching(false));
  }, [queryParams, refreshTrigger]);

  const complete = useCallback(async (id) => {
    try {
      await api.post(`/v1/performance-tasks/${id}/complete`);
      refresh();
      return true;
    } catch {
      return false;
    }
  }, [refresh]);

  // Gelen Kutum derin bağlantısı — tek görev kimlikle (id[eq]); yoksa null.
  const findById = useCallback(async (id) => {
    const res = await api.get(`/v1/performance-tasks?${new URLSearchParams({ 'id[eq]': id, pageNumber: 1, pageSize: 1 })}`);
    const result = res.data?.data;
    const items = Array.isArray(result) ? result : (result?.data || []);
    return items[0] || null;
  }, []);

  const columns = [
    { name: 'id', label: 'ID', options: { filter: false, sort: false, display: 'excluded' } },
    {
      name: 'taskType',
      label: t('performance.tasks.col.taskType'),
      options: {
        filter: true,
        sort: false,
        filterType: 'custom',
        customBodyRender: (v) => t(`performance.tasks.type.${v}`, { defaultValue: v }),
        customFilterListOptions: { render: (v) => t(`performance.tasks.type.${v}`, { defaultValue: v }) },
        filterOptions: {
          defaultOp: 'eq',
          display: (fl, onChange, i, col) => (
            <FormControl variant="standard" fullWidth>
              <InputLabel>{t('performance.tasks.col.taskType')}</InputLabel>
              <Select
                value={fl[i][0] || ''}
                onChange={(e) => {
                  fl[i] = e.target.value ? [e.target.value] : [];
                  onChange(fl[i], i, col);
                }}
              >
                <MenuItem value=""><em>{t('performance.tasks.filter.allTypes')}</em></MenuItem>
                {TASK_TYPES.map((tt) => (
                  <MenuItem key={tt} value={tt}>{t(`performance.tasks.type.${tt}`)}</MenuItem>
                ))}
              </Select>
            </FormControl>
          ),
        },
      },
    },
    {
      name: 'relatedEmployeeName',
      label: t('performance.tasks.col.relatedEmployeeName'),
      options: {
        customBodyRender: (v, tableMeta) => (
          <PersonCell name={v} profilePicture={data[tableMeta.rowIndex]?.relatedEmployeeProfilePicture} />
        ), filter: false, sort: false },
    },
    {
      name: 'title',
      label: t('performance.tasks.col.title'),
      options: { filter: false, sort: false },
    },
    {
      name: 'status',
      label: t('performance.tasks.col.status'),
      options: {
        filter: true,
        sort: false,
        filterType: 'custom',
        customBodyRender: (v) => (
          <StatusBadge $done={v === 'done'}>{t(`performance.tasks.status.${v}`, { defaultValue: v })}</StatusBadge>
        ),
        customFilterListOptions: { render: (v) => t(`performance.tasks.status.${v}`, { defaultValue: v }) },
        filterOptions: {
          defaultOp: 'eq',
          display: (fl, onChange, i, col) => (
            <FormControl variant="standard" fullWidth>
              <InputLabel>{t('performance.tasks.col.status')}</InputLabel>
              <Select
                value={fl[i][0] || ''}
                onChange={(e) => {
                  fl[i] = e.target.value ? [e.target.value] : [];
                  onChange(fl[i], i, col);
                }}
              >
                <MenuItem value=""><em>{t('performance.tasks.filter.allStatuses')}</em></MenuItem>
                {TASK_STATUSES.map((s) => (
                  <MenuItem key={s} value={s}>{t(`performance.tasks.status.${s}`)}</MenuItem>
                ))}
              </Select>
            </FormControl>
          ),
        },
      },
    },
    {
      name: 'createDate',
      label: t('performance.tasks.col.createDate'),
      options: { filter: false, sort: false, customBodyRender: (v) => formatDateTime(v, lang) },
    },
  ];

  return {
    fetching, data, pagination, columns, complete, refresh, findById,
  };
};

export default usePerformanceTasksApi;
